<script setup lang="ts">
import { LineupModel, LineupPlayer } from '@collector/desktop-feature-lineups'
import { runLoader, useConfirmModal } from '@collector/shared-ui'
import { SubParticipantPosition } from '@collector/sportsapi-client-legacy'
import { Participant, SubParticipant } from '@collector/sportsapi-types'
import { queryClient } from '@desktop/queryClient'
import { PlayersSecondaryPositionTable } from '@desktop/utils/database'
import { useEvent } from '@desktop/views/Relation/composables'
import { hasPositionChanged } from '@desktop/views/Relation/Sports/Baseball/configuration/lineupConfig'
import BaseLineupsModal from '@desktop/views/Relation/Sports/components/RelationView/Main/MainSummary/Lineups/BaseLineupsModal/BaseLineupsModal.vue'
import NoCoachConfirmModal from '@desktop/views/Relation/Sports/components/RelationView/Main/MainSummary/Lineups/ConfirmModals/NoCoachConfirmModal.vue'
import { UpdateLineupsFn } from '@desktop/views/Relation/Sports/composables'

import InvalidBattersCountConfirmModal from './components/ConfirmModals/InvalidBattersCountConfirmModal.vue'
import NoPitcherConfirmModal from './components/ConfirmModals/NoPitcherConfirmModal.vue'

interface Props {
  participant: Participant
}
const props = defineProps<Props>()

// composables
const { openModalForConfirm } = useConfirmModal()
const { event } = useEvent()

// methods
function hasPositionChanges(
  lineup: LineupModel,
  initialLineup: LineupModel,
): boolean {
  const keys = Object.keys(lineup) as PropertyKey[]

  return keys.some((key) => hasPositionChanged(key, lineup, initialLineup))
}

function getPossiblePitchers(lineup: LineupModel): LineupPlayer[] {
  return lineup.batter.filter(
    (player: LineupPlayer) =>
      player.has_secondary_position &&
      player.position !== SubParticipantPosition.Pitcher,
  )
}

async function confirmUpdateLineup(
  lineup: LineupModel,
  initialLineup: LineupModel,
  updateLineups: UpdateLineupsFn,
  _captain?: SubParticipant | null,
  isPendingDeferredClear?: boolean,
): Promise<void> {
  async function _updateLineups(): Promise<void> {
    if (hasPositionChanges(lineup, initialLineup) || isPendingDeferredClear) {
      // NOTE: send lineup update only if shirt number, position, or possible-batter
      // flag changed. Saving an unchanged lineup can reassign current batter /
      // current pitcher stats (bug in POST /sub-participants-save).
      // Exception: pending probable-lineup deferred clear still requires save +
      // clear-lineup even when the rebuilt squad matches the pre-clear baseline.
      await updateLineups(event.value.id, props.participant.id, lineup)
    }

    // Possible pitchers (BAT → PIT) have no API flag; keep them in local storage.
    const possiblePitchers = getPossiblePitchers(lineup).map(
      (player: LineupPlayer) => ({
        shortName: player.short_name,
        id: player.id,
      }),
    )

    await PlayersSecondaryPositionTable.setPlayers(
      event.value.id,
      props.participant.id,
      possiblePitchers,
    )

    // Save returns empty data — reload flags from GET /sub-participants.
    await queryClient.refetchQueries({
      queryKey: ['event', 'subParticipants', event.value.id],
    })
  }

  const coachCount = lineup.coach.length
  if (coachCount === 0) await openModalForConfirm(NoCoachConfirmModal)

  const pitchersCount = lineup.pitcher.length
  if (pitchersCount === 0) await openModalForConfirm(NoPitcherConfirmModal)

  const battersCount = lineup.batter.length
  if (battersCount < 8)
    await openModalForConfirm(InvalidBattersCountConfirmModal)

  await runLoader(_updateLineups())
}
</script>

<template>
  <BaseLineupsModal
    :participant
    :confirmUpdateLineup
  />
</template>
